import i18next from 'i18next';
import { toast } from 'sonner';
import { useVulnerabilityStore } from '../../store/vulnerability.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class VulnerabilityIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * List for embedded UIs (e.g. asset detail) without mutating the global vulnerability store.
     */
    async fetchListDetached(
        page = 1,
        pageSize = 10,
        search = '',
        sortBy = 'createdAt',
        sortOrder = 'desc',
        columnFilters = {},
        options = {}
    ) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], meta: { total: 0, page: 1, pages: 0 }, uniqueValues: {} };
        }
        try {
            const filterParams = Object.entries(columnFilters || {}).reduce((acc, [key, value]) => {
                if (value && value.length > 0) acc[`filter_${key}`] = value;
                return acc;
            }, {});
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...(options.assetId ? { assetId: options.assetId } : {}),
                ...filterParams,
            });
            const pagination = response.pagination || {};
            return {
                success: true,
                data: response.data || [],
                meta: {
                    total: pagination.total ?? 0,
                    page: pagination.page ?? page,
                    pages: pagination.totalPages ?? 0,
                },
                uniqueValues: response.uniqueValues || {},
            };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            return { success: false, data: [], meta: { total: 0 }, uniqueValues: {}, error: msg };
        }
    }

    async getById(id, opts = {}) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadRecord', { defaultValue: 'Could not load record' });
            if (!opts.skipErrorToast) {
                toast.error(msg);
            }
            return { success: false, error: msg };
        }
    }

    async fetchAll(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'desc', columnFilters = {}, options = {}) {
        const store = useVulnerabilityStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        store.clearError();
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...(options.assetId ? { assetId: options.assetId } : {}),
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });
            const { data, pagination, uniqueValues } = response;
            store.setItems(data || []);
            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                });
            }
            if (uniqueValues) store.setUniqueValues(uniqueValues);
            return { success: true };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            store.setError(msg);
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        const store = useVulnerabilityStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:recordCreated', { defaultValue: 'Record created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotCreateRecord', { defaultValue: 'Could not create record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    async update(id, data, opts = {}) {
        const store = useVulnerabilityStore.getState();
        if (!opts.skipStoreLoading) store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            if (!opts.skipSuccessToast) {
                toast.success(i18next.t('integrationToasts:recordUpdated', { defaultValue: 'Record updated' }));
            }
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotUpdateRecord', { defaultValue: 'Could not update record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            if (!opts.skipStoreLoading) store.setLoading(false);
        }
    }

    async delete(id) {
        const store = useVulnerabilityStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:recordDeleted', { defaultValue: 'Record deleted' }));
            return { success: true, data: response.data };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotDeleteRecord', { defaultValue: 'Could not delete record' });
            toast.error(msg);
            return { success: false, error: msg };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Records for a single asset (no global store update). Used from asset detail exposure tab.
     */
    async listForAsset(assetId, opts = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !assetId) return { success: false, data: [] };
        try {
            const pageSize = opts.pageSize ?? 50;
            const response = await this.service.getAll({
                tenantId,
                assetId,
                page: 1,
                pageSize,
                limit: pageSize,
                sortBy: opts.sortBy ?? 'updatedAt',
                sortOrder: opts.sortOrder ?? 'desc',
            });
            return { success: true, data: response.data || [] };
        } catch (error) {
            const msg = getIntegrationErrorMessage(error) || i18next.t('integrationToasts:couldNotLoadList', { defaultValue: 'Could not load list' });
            return { success: false, data: [], error: msg };
        }
    }
}
